import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

export default function AcceptInvitation() {
    const { token } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [invitation, setInvitation] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');        // load-time error → full-page fallback
    const [acceptError, setAcceptError] = useState(''); // transient accept error → inline
    const [accepting, setAccepting] = useState(false);

    useEffect(() => {
        fetchInvitation();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const fetchInvitation = async () => {
        try {
            const response = await api.get(`/invitations/${token}`);
            setInvitation(response.data);
        } catch (err) {
            setError(err.response?.data?.error || 'Invalid or expired invitation');
        } finally {
            setLoading(false);
        }
    };

    const handleAcceptInvitation = async () => {
        if (!user) {
            // Redirect to login with return URL
            navigate(`/login?redirect=/invite/${token}`);
            return;
        }

        setAccepting(true);
        setAcceptError('');

        try {
            await api.post(`/invitations/${token}/accept`);
            navigate('/dashboard');
        } catch (err) {
            // Keep the invite screen visible and let the user retry, rather than
            // replacing it with the "Invalid Invitation" full-page fallback.
            setAcceptError(err.response?.data?.error || 'Failed to accept invitation. Please try again.');
            setAccepting(false);
        }
    };

    // Sign-in / sign-up links that return to this invite and prefill the invited
    // email, so the visitor doesn't have to retype it.
    const emailParam = invitation?.email ? `&email=${encodeURIComponent(invitation.email)}` : '';
    const signInLink = `/login?redirect=/invite/${token}${emailParam}`;
    const signUpLink = `/register?redirect=/invite/${token}${emailParam}`;

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-gray-600">Loading invitation...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
                <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8 text-center">
                    <div className="text-red-600 text-5xl mb-4">⚠️</div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">Invalid Invitation</h1>
                    <p className="text-gray-600 mb-6">{error}</p>
                    <Link to="/dashboard" className="text-blue-600 hover:text-blue-700 font-medium">
                        Go to Dashboard
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4">
            <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8">
                <div className="text-center mb-6">
                    <div className="text-5xl mb-4">🎁</div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">Group Invitation</h1>
                    <p className="text-gray-600">
                        You've been invited to join <span className="font-semibold">{invitation?.group?.name}</span>
                    </p>
                </div>

                {invitation && (
                    <div className="bg-gray-50 rounded-lg p-4 mb-6">
                        <p className="text-sm text-gray-600 mb-1">Invited by:</p>
                        <p className="font-semibold text-gray-900">{invitation.invited_by?.name}</p>
                        {invitation.email && (
                            <>
                                <p className="text-sm text-gray-600 mt-3 mb-1">Invitation for:</p>
                                <p className="font-semibold text-gray-900">{invitation.email}</p>
                            </>
                        )}
                        <p className="text-sm text-gray-600 mt-3 mb-1">Current members:</p>
                        <p className="font-semibold text-gray-900">
                            {invitation.group?.members_count ?? invitation.group?.members?.length ?? 0} member(s)
                        </p>
                    </div>
                )}

                {!user ? (
                    // A fresh invitee almost never has an account yet, so default to
                    // sign-up. If the invited email is already registered, lead with
                    // sign-in instead. Either way we carry the email into the form.
                    invitation?.invitee_registered ? (
                        <div className="space-y-3">
                            <p className="text-sm text-gray-600 text-center mb-4">
                                You already have an account — sign in to accept.
                            </p>
                            <Link
                                to={signInLink}
                                className="block w-full text-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
                            >
                                Sign In to Accept
                            </Link>
                            <Link to={signUpLink} className="block text-center text-sm text-blue-600 hover:text-blue-700">
                                Use a different account? Sign up
                            </Link>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            <p className="text-sm text-gray-600 text-center mb-4">
                                Create your account to join the group.
                            </p>
                            <Link
                                to={signUpLink}
                                className="block w-full text-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
                            >
                                Create Account to Accept
                            </Link>
                            <Link to={signInLink} className="block text-center text-sm text-blue-600 hover:text-blue-700">
                                Already have an account? Sign in
                            </Link>
                        </div>
                    )
                ) : (
                    <div className="space-y-3">
                        <p className="text-sm text-gray-600 text-center mb-4">
                            Signed in as: <span className="font-semibold">{user.name}</span>
                        </p>
                        {acceptError && (
                            <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm text-center">
                                {acceptError}
                            </div>
                        )}
                        <button
                            onClick={handleAcceptInvitation}
                            disabled={accepting}
                            className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {accepting ? 'Accepting...' : 'Accept Invitation'}
                        </button>
                        <Link
                            to="/dashboard"
                            className="block text-center text-sm text-gray-600 hover:text-gray-700"
                        >
                            Not you? Sign in with a different account
                        </Link>
                    </div>
                )}

                {invitation && (
                    <p className="text-xs text-gray-500 text-center mt-6">
                        Invitation expires {new Date(invitation.expires_at).toLocaleDateString()}
                    </p>
                )}
            </div>
        </div>
    );
}